import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

import { BACKEND_URL } from '@/config';
const API = `${BACKEND_URL}/api`;

const CmsContext = createContext(null);

export const useCms = () => {
  const context = useContext(CmsContext);
  if (!context) {
    throw new Error('useCms must be used within a CmsProvider');
  }
  return context;
};

export const CmsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [navigation, setNavigation] = useState({ header: [], footer: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCmsData();
  }, []);

  const fetchCmsData = async () => {
    try {
      const [settingsRes, navRes] = await Promise.all([
        axios.get(`${API}/cms/settings`),
        axios.get(`${API}/cms/pages/navigation`),
      ]);
      setSettings(settingsRes.data);
      setNavigation(navRes.data);
    } catch (error) {
      console.error('Failed to fetch CMS data:', error);
      // Use default settings
      setSettings({
        site_name: 'Common Impact E-learning',
        site_tagline: 'Training for the Care Sector',
        primary_color: '#20acf6',
        secondary_color: '#02d1d4',
        background_color: '#F8FAFC',
        card_color: '#f8fafc',
        text_color: '#1e293b',
        homepage_hero_title: 'Empowering Care Through Learning',
        homepage_hero_subtitle: 'Professional, accredited training for care workers, support staff, and organisations',
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshCms = () => {
    fetchCmsData();
  };

  // Apply CSS variables from settings
  useEffect(() => {
    if (settings) {
      const root = document.documentElement;
      if (settings.primary_color) {
        root.style.setProperty('--ci-blue', settings.primary_color);
      }
      if (settings.secondary_color) {
        root.style.setProperty('--ci-teal', settings.secondary_color);
      }
      if (settings.background_color) {
        root.style.setProperty('--ci-off-white', settings.background_color);
      }
      if (settings.text_color) {
        root.style.setProperty('--ci-slate', settings.text_color);
      }
    }
  }, [settings]);

  const value = {
    settings,
    navigation,
    loading,
    refreshCms,
  };

  return (
    <CmsContext.Provider value={value}>
      {children}
    </CmsContext.Provider>
  );
};

export default CmsContext;
